/******* Angular Resourse *******/
import { Inject, Injectable, Optional } from '@angular/core';
import { BaseApi } from '@api/base/base.service';
import { HttpClient } from '@angular/common/http';
import { BatchUpdateDeviceRequestBody, DeleteDeviceRequestBody, LicenseStatusValue, UpdateConfigRequestBody, UpdateDataUsageSettingsRequestBody, UpdateDeviceRequestBody, WebProtocol } from '@core/types';
import { AccessTokenService } from '@core/services/access-token.service';

@Injectable()
export class Devices extends BaseApi {

    constructor(
        @Inject(HttpClient) protected http: HttpClient,
        private _accessTokenService: AccessTokenService
    ) {
        super(http);
    }

    /**
     * GET /devices - get devices list
     * 
     * @param filter
     */
    public getDevices(filter) {
        return this.find("devices", filter);
    }

    /**
     * GET /devices/count - get devices list count
     * 
     * @param where
     */
    public getDevicesCount(where) {
        return this.count("devices", where);
    }

    /**
     * GET /devices/{id} - get single device information by id
     * 
     * @param id
     */
    public getDeviceInfo(id) {
        return this.findById("devices", id);
    }

    /**
     * GET /devices/{MAC}/find-cell-tower - get cell tower data by MAC
     * 
     * @param {string} MAC
     */
    public getCellDataByMac(MAC: string) {

        let method = "GET",
            url = "devices/:MAC/find-cell-tower",
            routeParams = { MAC },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * DELETE /devices - delete device(s)
     * 
     * @param {DeleteDeviceRequestBody} data
     */
    public deleteDeviceInBatch(data: DeleteDeviceRequestBody) {

        let method = "DELETE_WITH_BODY",
            url = "devices",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PATCH /devices/{id} - update single device information by id
     * 
     * @param {string} id
     * @param {UpdateDeviceRequestBody} data
     */
    public updateDevice(id: string, data: UpdateDeviceRequestBody) {

        let method = "PATCH",
            url = "devices/:id",
            routeParams = { id },
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{id}/configuration - get configuration by id
     * 
     * @param {string} id
     */
    public getConfiguration(id: string) {

        let method = "GET",
            url = "devices/:id/configuration",
            routeParams = { id },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PATCH /devices/{id}/configuration - set configuration by id
     * 
     * @param {string} id
     * @param {UpdateConfigRequestBody} data
     */
    public setConfiguration(id: string, data: UpdateConfigRequestBody) {

        let method = "PATCH",
            url = "devices/:id/configuration",
            routeParams = { id },
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{id}/configuration/sync - forcing pulling configuration from device
     * 
     * @param {string} id
     */
    public getConfigurationFromLocal(id: string) {

        let method = "GET",
            url = "devices/:id/configuration/sync",
            routeParams = { id },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/devCarrierSummary - get connected devices by carrier
     * 
     * @param {any} groupIdFilter
     */
    public getConnectedDeviceByCarrier(groupIdFilter: any = {}) {

        let method = "GET",
            url = "devices/devCarrierSummary",
            routeParams = {},
            urlParams = groupIdFilter,
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/devModelSummary - get connected devices by model
     * 
     * @param {any} groupIdFilter
     */
    public getConnectedDeviceByModel(groupIdFilter: any = {}) {

        let method = "GET",
            url = "devices/devModelSummary",
            routeParams = {},
            urlParams = groupIdFilter,
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/devNetModeSummary - get connected devices by network type
     * 
     * @param {any} groupIdFilter
     */
    public getConnectedDeviceByNetworkType(groupIdFilter: any = {}) {

        let method = "GET",
            url = "devices/devNetModeSummary",
            routeParams = {},
            urlParams = groupIdFilter,
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/revisionStats - get connected devices by radio type
     * 
     * @param {any} groupIdFilter
     */
    public getConnectedDeviceByRadioType(groupIdFilter: any = {}) {

        let method = "GET",
            url = "devices/revisionStats",
            routeParams = {},
            urlParams = groupIdFilter,
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{attr}/unique - get unique value in devices
     * 
     * @param attr
     * @param groupIdFilter
     */
    public getUniqueKey(attr: string, groupIdFilter: any = {}) {

        let method = "GET",
            url = "devices/:attr/unique",
            routeParams = { attr },
            urlParams = {
                filter: {
                    "where": {
                        "connected": { "$in": [true, false] },
                        "licenseStatus": LicenseStatusValue.ACTIVE,
                        "MODEL": { "$nin": [null, ""] },
                        "MAC": { "$nin": [null, ""] },
                        ...groupIdFilter
                    }
                }
            },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/tags - get devices list with tags
     * 
     * @param filter
     */
    public getDevicesWithTags(filter: any) {

        let method = "GET",
            url = "devices/tags",
            routeParams = {},
            urlParams = { filter },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/tags/count - get devices list with tags
     * 
     * @param filter
     */
    public getDevicesWithTagsCount(filter: any) {

        let method = "GET",
            url = "devices/tags/count",
            routeParams = {},
            urlParams = { filter },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/cell-analysis/count - get count in cell analysis of devices
     */
    public getDevicesCellAnalysisCount() {

        let method = "GET",
            url = "devices/cell-analysis/count",
            routeParams = {},
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/cell-analysis - get cell analysis of devices
     * 
     * @param filter
     */
    public getDevicesCellAnalysis(filter: any) {

        let method = "GET",
            url = "devices/cell-analysis",
            routeParams = {},
            urlParams = { filter },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{id}/getTrackDataUsages - get device data usage
     * 
     * @param {string} id 
     */
    public getDeviceDataUsage(id: string) {

        let method = "GET",
            url = "devices/:id/getTrackDataUsages",
            routeParams = { id },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * POST /alert-config/{id}/updateDataUsageSetting - update device data usage settings
     * 
     * @param {string} id
     * @param {UpdateDataUsageSettingsRequestBody} data
     */
    public updateDeviceDataUsage(id: string, data: UpdateDataUsageSettingsRequestBody) {

        let method = "POST",
            url = "alert-config/:id/updateDataUsageSetting",
            routeParams = { id },
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{MAC}/speedtest/count - get speed test records count
     * 
     * @param {string} MAC 
     */
    public getDeviceSpeedTestRecordsCount(MAC: string) {

        let method = "GET",
            url = "devices/:MAC/speedtest/count",
            routeParams = { MAC },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{MAC}/speedtest - get speed test records 
     * 
     * @param {string} MAC 
     * @param filter
     */
    public getDeviceSpeedTestRecords(MAC: string, filter: any) {

        let method = "GET",
            url = "devices/:MAC/speedtest",
            routeParams = { MAC },
            urlParams = { filter },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * DELETE /devices/{MAC}/speedtest - remove all speed test records by MAC
     * 
     * @param {string} MAC
     */
    public deleteDeviceSpeedTestRecords(MAC: string) {

        let method = "DELETE",
            url = "devices/:MAC/speedtest",
            routeParams = { MAC },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{MAC}/histories - get rf histories 
     * 
     * @param {string} MAC 
     * @param {string} startDate
     * @param {string} endDate
     */
    public getDeviceRfHistories(MAC: string, startDate: string, endDate: string) {

        let method = "GET",
            url = "devices/:MAC/histories",
            routeParams = { MAC },
            urlParams = { startDate, endDate },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{MAC}/remote-access - get remote-access from device 
     * 
     * @param {string} MAC
     */
    public getDeviceRemoteAccess(MAC: string, ipaddress: string = '127.0.0.1', port: string = '80', protocol: WebProtocol = WebProtocol.HTTP) {

        let method = "GET",
            url = "devices/:MAC/remote-access",
            routeParams = { MAC },
            urlParams = { ipaddress, port, protocol },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * DELETE /devices/{MAC}/remote-access - disable remote-access from device 
     * 
     * @param {string} MAC
     */
    public deleteDeviceRemoteAccess(MAC: string) {

        let method = "DELETE",
            url = "devices/:MAC/remote-access",
            routeParams = { MAC },
            urlParams = {},
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * GET /devices/{MAC}/remote-ssh - get remote-ssh from device 
     * 
     * @param {string} MAC
     */
    public getDeviceRemoteSsh(MAC: string, ipaddress: string = '127.0.0.1', username: string = 'admin') {

        let method = "GET",
            url = "devices/:MAC/remote-ssh",
            routeParams = { MAC },
            urlParams = { ipaddress, username },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    /**
     * PATCH /devices - update multiple device information
     * 
     * @param {BatchUpdateDeviceRequestBody} data
     */
    public batchUpdateDevice(data: BatchUpdateDeviceRequestBody) {

        let method = "PATCH",
            url = "devices",
            routeParams = {},
            urlParams = {},
            postBody = data;

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    public getOktopusDevices(pageNumber: number = 1, pageSize: number = 20, filters: any = {}) {

        let method = "GET",
            url = "tr369/devices",
            routeParams = {},
            urlParams = { page_number: pageNumber, page_size: pageSize, ...filters },
            postBody = {};

        return this.request(method, url, routeParams, urlParams, postBody);
    }

    public getOktopusDeviceById(sn: string) {
        return this.httpClient.get(`/api/tr369/device`, { params: { id: sn } });
    }

    public getOktopusParameterValues(sn: string) {
        const body = `<?xml version="1.0" encoding="UTF-8"?>\n<soap:Envelope xmlns:soapenc="http://schemas.xmlsoap.org/soap/encoding/" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:cwmp="urn:dslforum-org:cwmp-1-0" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:schemaLocation="urn:dslforum-org:cwmp-1-0 ..schemaswt121.xsd" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\n  <soap:Header/>\n  <soap:Body soap:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">\n    <cwmp:GetParameterValues>\n      <ParameterNames soapenc:arrayType="xsd:string[1]"><string>InternetGatewayDevice.</string></ParameterNames>\n    </cwmp:GetParameterValues>\n  </soap:Body>\n</soap:Envelope>`;
        return this.httpClient.put(`/api/devices/tr369/proxy/api/device/cwmp/${encodeURIComponent(sn)}/getParameterValues`, body, {
            responseType: 'text',
            headers: {
                'Content-Type': 'application/xml',
                'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
            }
        });
    }

    public getOktopusParameterNames(sn: string) {
        const body = `<?xml version="1.0" encoding="UTF-8"?>\n<soap:Envelope xmlns:soapenc="http://schemas.xmlsoap.org/soap/encoding/" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:cwmp="urn:dslforum-org:cwmp-1-0" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:schemaLocation="urn:dslforum-org:cwmp-1-0 ..schemaswt121.xsd" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">\n  <soap:Header/>\n  <soap:Body soap:encodingStyle="http://schemas.xmlsoap.org/soap/encoding/">\n    <cwmp:GetParameterNames>\n      <ParameterPath>InternetGatewayDevice.</ParameterPath>\n      <NextLevel>1</NextLevel>\n    </cwmp:GetParameterNames>\n  </soap:Body>\n</soap:Envelope>`;
        return this.httpClient.put(`/api/devices/tr369/proxy/api/device/cwmp/${encodeURIComponent(sn)}/getParameterNames`, body, {
            responseType: 'text',
            headers: {
                'Content-Type': 'application/xml',
                'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
            }
        });
    }

    public getUspAnyParameters(sn: string, objPath: string = 'Device.') {
        const body = {
            obj_paths: [objPath],
            first_level_only: true,
            return_commands: true,
            return_events: true,
            return_params: true
        };
        return this.httpClient.put(
            `/api/devices/tr369/proxy/api/device/${encodeURIComponent(sn)}/any/parameters`,
            body,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
                }
            }
        );
    }

    public getUspParameterValues(sn: string, paramPaths: string[]) {
        return this.httpClient.put(
            `/api/devices/tr369/proxy/api/device/${encodeURIComponent(sn)}/any/get`,
            { param_paths: paramPaths, max_depth: 1 },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
                }
            }
        );
    }

    public setUspParameter(sn: string, objPath: string, paramName: string, value: string) {
        const body = {
            allow_partial: true,
            update_objs: [
                {
                    obj_path: objPath,
                    param_settings: [
                        { param: paramName, value, required: true }
                    ]
                }
            ]
        };
        return this.httpClient.put(
            `/api/devices/tr369/proxy/api/device/${encodeURIComponent(sn)}/any/set`,
            body,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
                }
            }
        );
    }

    public getRpcTemplates() {
        return this.httpClient.get<{ tr369: any[]; cwmp: any[] }>('/api/devices/rpc/templates', {
            headers: { 'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}` }
        });
    }

    public getRpcCustomTemplates() {
        return this.httpClient.get<any[]>('/api/devices/rpc/custom-templates', {
            headers: { 'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}` }
        });
    }

    public createRpcCustomTemplate(name: string, protocol: string, payload: string) {
        return this.httpClient.post<any>('/api/devices/rpc/custom-templates', { name, protocol, payload }, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
            }
        });
    }

    public deleteRpcCustomTemplate(id: string) {
        return this.httpClient.delete<void>(`/api/devices/rpc/custom-templates/${id}`, {
            headers: { 'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}` }
        });
    }

    public sendRpc(body: { templateId?: string; payload?: string; protocol?: string; devicePath: string }) {
        return this.httpClient.post<any>('/api/devices/rpc/send', body, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this._accessTokenService.currentAccessTokenValue}`
            }
        });
    }

}
